import { useTranslation } from 'react-i18next';

import { AnimationShell, Button, Checkbox, Field, Form, Input, Link, Typography } from '@/components';
import { Route } from '@/constants';
import { ShowPassword } from '../show-password';
import { useState } from 'react';
import { useValidatePassword } from '../../hooks/use-validate-password';
import { PasswordRulesList } from '../password-rules-list';
import { Turnstile } from '../turnstile-check';
import { isVerySuspiciousName } from '@/pages/auth/helpers';
import { TURNSTILE_SITE_ID } from '@/config';

export type UserFormFields = {
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  password: string;
  confirmed_password: string;
  show_modal_ttcc: boolean;
  accepted_ttcc: boolean;
  turnstileToken: string;
  honeypot?: string;
   // UTM tracking:
   origin?: string;
   campaign?: string;
};

export type UserFormProps = {
  readonly onSubmit: (data: UserFormFields) => void;
};

export function UserForm({ onSubmit }: UserFormProps) {
  const { t } = useTranslation(['signup']);
  const { i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const termsUrl =
    window.location.hostname === 'localhost' || window.location.hostname.includes('sandbox')
      ? `https://sandbox.nteaser.com/${i18n.language}/terms-and-conditions`
      : `https://nteaser.com/${i18n.language}/terms-and-conditions`;

  const privacyUrl =
    window.location.hostname === 'localhost' || window.location.hostname.includes('sandbox')
      ? `https://sandbox.nteaser.com/${i18n.language}/privacy-policy`
      : `https://nteaser.com/${i18n.language}/privacy-policy`;

  return (
    <Form<UserFormFields> onSubmit={onSubmit}>
      {({ watch, register, formState, setValue }) => {
        const password = watch('password') || '';
        const { hasMinLength, hasUppercase, hasLowercase, hasNumber, hasSpecialChar, isValid } =
          useValidatePassword(password);
        const [isTouched, setIsTouched] = useState(false);
        const showPasswordRules = isTouched || (!!password && !isValid);

        const turnstileToken = watch('turnstileToken');

        return (
          <div className="flex flex-col space-y-4">
            {/* HONEYPOT: */}
            <div
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
              }}
              aria-hidden="true"
            >
              <label htmlFor="website">Website</label>
              <input id="website" type="text" autoComplete="off" tabIndex={-1} {...register('honeypot')} />
            </div>
            {/* END HONEYPOT: */}

            <Field
              id="name"
              label={t('Company name')}
              error={formState.errors['name']}
              delay={0}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                color={formState.errors['name'] ? 'error' : 'primary'}
                autoComplete="off"
                registration={register('name', { required: t('This field is required') })}
              />
            </Field>

            <div className="inline-flex w-full items-start gap-x-4">
              <Field
                id="first_name"
                label={t('First name')}
                error={formState.errors['first_name']}
                delay={0}
                classes={{ field: 'w-1/2 space-y-1' }}
              >
                <Input
                  color={formState.errors['first_name'] ? 'error' : 'primary'}
                  placeholder="John"
                  autoComplete="given-name"
                  registration={register('first_name', {
                    required: t('This field is required'),
                    validate: (value) => !isVerySuspiciousName(value),
                  })}
                />
              </Field>

              <Field
                id="last_name"
                label={t('Last name')}
                error={formState.errors['last_name']}
                delay={0.15}
                classes={{ field: 'w-1/2 space-y-1' }}
              >
                <Input
                  color={formState.errors['last_name'] ? 'error' : 'primary'}
                  placeholder="Doe"
                  autoComplete="family-name"
                  registration={register('last_name', {
                    required: false,
                    validate: (value) => !isVerySuspiciousName(value),
                  })}
                />
              </Field>
            </div>

            <div className="inline-flex w-full items-start gap-x-4">
              <Field
                id="email"
                label={t('Email address')}
                error={formState.errors['email']}
                delay={0.25}
                classes={{ field: 'w-1/2 space-y-1' }}
              >
                <Input
                  type="email"
                  color={formState.errors['email'] ? 'error' : 'primary'}
                  placeholder="john.doe@example.com"
                  autoComplete="email"
                  registration={register('email', { required: t('This field is required') })}
                />
              </Field>
              <Field
                id="phone_number"
                label={t('Phone')}
                error={formState.errors['phone_number']}
                delay={0.25}
                classes={{ field: 'w-1/2 space-y-1' }}
              >
                <Input
                  type="tel"
                  color={formState.errors['phone_number'] ? 'error' : 'primary'}
                  placeholder="e.g. +34123456789"
                  autoComplete="phone"
                  registration={register('phone_number', {
                    pattern: {
                      value: /^\+?[0-9\s-]+$/,
                      message: "Invalid format. Only numbers, spaces, dashes, and an initial '+' are allowed",
                    },
                    maxLength: {
                      value: 20,
                      message: 'The phone number cannot have more than 20 characters',
                    },
                  })}
                />
              </Field>
            </div>

            <Field
              id="password"
              label={t('Password')}
              error={formState.errors['password']}
              delay={0.35}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                color={formState.errors['password'] ? 'error' : 'primary'}
                registration={register('password', {
                  required: t('This field is required'),
                  validate: () =>
                    (hasUppercase && hasLowercase && hasNumber && hasSpecialChar) ||
                    t('Password does not meet all requirements'),
                })}
                rightElement={<ShowPassword showPassword={showPassword} setShowPassword={setShowPassword} />}
                onFocus={() => setIsTouched(true)}
              />
            </Field>

            {showPasswordRules && (
              <PasswordRulesList
                hasMinLength={hasMinLength}
                hasUppercase={hasUppercase}
                hasLowercase={hasLowercase}
                hasNumber={hasNumber}
                hasSpecialChar={hasSpecialChar}
              />
            )}

            <Field
              id="confirmed_password"
              label={t('Repeat password')}
              error={formState.errors['confirmed_password']}
              delay={0.45}
              classes={{ field: 'space-y-1' }}
            >
              <Input
                type={showRepeatPassword ? 'text' : 'password'}
                autoComplete="new-password"
                color={formState.errors['confirmed_password'] ? 'error' : 'primary'}
                registration={register('confirmed_password', {
                  required: t('This field is required'),
                  validate: (value) => value === watch('password') || t('The passwords do not match'),
                })}
                rightElement={
                  <ShowPassword showPassword={showRepeatPassword} setShowPassword={setShowRepeatPassword} />
                }
              />
            </Field>

            <Field
              id="accepted_ttcc"
              error={formState.errors['accepted_ttcc']}
              delay={0.6}
              classes={{ field: 'space-y-1' }}
            >
              <Checkbox
                id="accepted_ttcc"
                color="primary"
                registration={register('accepted_ttcc', {
                  required: t('You must accept the terms and conditions to register.'),
                })}
              >
                <Typography as="p" color="secondary" size="body-3" weight="normal">
                  {t("I read and accept nTeaser's ")}
                  <Link to={termsUrl} target="_blank">
                    {t('Terms and Conditions')}
                  </Link>
                  {t(' and ')}
                  <Link to={privacyUrl} target="_blank">
                    {t('Privacy Policy')}
                  </Link>
                  {t('.')}
                </Typography>
              </Checkbox>
            </Field>

            <AnimationShell type="opacity" delay={0.55}>
              <Button type="submit" size="lg" className="w-full" disabled={!turnstileToken || formState.isSubmitting}>
                {t('Continue')}
              </Button>
            </AnimationShell>

            <Field
              id="turnstile"
              error={formState.errors['turnstileToken']}
              delay={0.65}
              classes={{ field: 'space-y-1' }}
            >
              <input
                type="hidden"
                {...register('turnstileToken', {
                  required: t('Please complete the security check.'),
                })}
              />
              <Turnstile
                siteKey={TURNSTILE_SITE_ID}
                onToken={(token) => {
                  setValue('turnstileToken', token, { shouldValidate: true });
                }}
              />
            </Field>
          </div>
        );
      }}
    </Form>
  );
}
